import { useState, useRef, useCallback, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Button, Empty, Popover, Spin } from "antd";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { alertMessage } from "../../utils/alert";
import {
	DownloadOutlined,
	PrinterOutlined,
	ZoomInOutlined,
	ZoomOutOutlined,
} from "@ant-design/icons";

// Set worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type PdfViewerProps = {
	title?: string;
	fileUrl: string | null;
	open?: boolean;
	onClose?: () => void;
	toolbarLeft?: React.ReactNode;
	// Off where the page around the viewer has its own Download.
	showDownload?: boolean;
};

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const SCALE_STEP = 0.25;

const SCALE_OPTIONS = [
	{ value: "100", label: "100%" },
	{ value: "125", label: "125%" },
	{ value: "150", label: "150%" },
	{ value: "175", label: "175%" },
	{ value: "200", label: "200%" },
	{ value: "250", label: "250%" },
	{ value: "300", label: "300%" },
];

const CustomPdfViewer = ({
	fileUrl,
	title = "PDF Viewer",
	toolbarLeft,
	showDownload = true,
}: PdfViewerProps) => {
	const [numPages, setNumPages] = useState<number>(0);
	const [scale, setScale] = useState<number>(1);
	const [scaleInput, setScaleInput] = useState<string>("100%");
	const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
	const [pageWidth, setPageWidth] = useState<number>(0);
	const [error, setError] = useState<string | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const scaleDropdownRef = useRef<HTMLDivElement>(null);
	const scaleInputRef = useRef<HTMLInputElement>(null);
	const skipBlurCommitRef = useRef(false);

	// Close dropdown on click outside
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (
				scaleDropdownRef.current &&
				!scaleDropdownRef.current.contains(e.target as Node)
			) {
				setDropdownOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const onPageLoadSuccess = (page: { width: number }) => {
		if (containerRef.current && pageWidth === 0) {
			const containerW = containerRef.current.clientWidth - 32; // minus padding
			const fitScale = Math.max(containerW / page.width, MIN_SCALE);
			const clamped = Math.min(fitScale, 1.5);
			setScale(clamped);
			setScaleInput(`${Math.round(clamped * 100)}%`);
			setPageWidth(page.width);
		}
	};

	const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
		setNumPages(numPages);
		setError(null);
	};

	const onDocumentLoadError = (err: Error) => {
		setError(err.message);
	};

	const applyScale = (newScale: number) => {
		const clamped = Math.max(0.01, newScale);
		setScale(clamped);
		setScaleInput(`${Math.round(clamped * 100)}%`);
	};

	const zoomIn = () => {
		applyScale(scale + SCALE_STEP);
	};

	const zoomOut = () => {
		applyScale(scale - SCALE_STEP);
	};

	const handleScaleCommit = (value: string) => {
		const num = parseFloat(value.replace("%", "").trim());
		if (!isNaN(num) && num > 0) {
			applyScale(num / 100);
		} else {
			// Reset to current scale if invalid
			setScaleInput(`${Math.round(scale * 100)}%`);
		}
	};

	const handleOptionClick = (value: string) => {
		handleScaleCommit(value);
		setDropdownOpen(false);
		skipBlurCommitRef.current = true;
		scaleInputRef.current?.blur();
	};

	const handleScaleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			handleScaleCommit(scaleInput);
			setDropdownOpen(false);
			scaleInputRef.current?.blur();
		} else if (e.key === "Escape") {
			// Only resets the zoom: a modal around the viewer must not close.
			e.stopPropagation();
			setScaleInput(`${Math.round(scale * 100)}%`);
			setDropdownOpen(false);
			scaleInputRef.current?.blur();
		}
	};

	const handleScaleBlur = () => {
		if (skipBlurCommitRef.current) {
			skipBlurCommitRef.current = false;
			return;
		}
		// Delay to allow dropdown click to register first
		setTimeout(() => {
			if (!scaleDropdownRef.current?.contains(document.activeElement)) {
				handleScaleCommit(scaleInput);
				setDropdownOpen(false);
			}
		}, 150);
	};

	const handlePrint = useCallback(async () => {
		if (!fileUrl) return;

		try {
			const response = await fetch(fileUrl);
			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);

			const iframe = document.createElement("iframe");
			iframe.style.display = "none";
			iframe.src = url;

			document.body.appendChild(iframe);

			iframe.onload = () => {
				iframe.contentWindow?.print();
				// Clean up after print dialog closes
				setTimeout(() => {
					document.body.removeChild(iframe);
					window.URL.revokeObjectURL(url);
				}, 1000);
			};
		} catch (err) {
			alertMessage("error", "Print failed");
		}
	}, [fileUrl]);

	const handleDownload = useCallback(async () => {
		if (!fileUrl) return;
		try {
			const response = await fetch(fileUrl);
			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = title || "document.pdf";
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);
		} catch (err) {
			alertMessage("error", "Download failed");
		}
	}, [fileUrl, title]);

	return (
		<div className="h-full w-full py-5 px-[30px]">
			<div className="flex h-full max-h-full flex-col">
				{/* Toolbar */}
				<div className="flex shrink-0 items-center justify-between gap-2 bg-(--secondary-color) text-white py-3 px-2">
					{/* Left side — breadcrumb slot */}
					<div className="flex items-center gap-2 overflow-hidden">
						{toolbarLeft}
					</div>

					{/* Right side — controls */}
					<div className="flex items-center gap-2">
						{/* Zoom Controls */}
						<div className="flex flex-nowrap items-center gap-1">
							<Button
								className="text-white!"
								type="text"
								icon={<ZoomOutOutlined />}
								onClick={zoomOut}
								disabled={scale <= MIN_SCALE}
							/>
							<div ref={scaleDropdownRef} className="relative">
								<input
									ref={scaleInputRef}
									type="text"
									value={scaleInput}
									onChange={(e) => setScaleInput(e.target.value)}
									onFocus={() => setDropdownOpen(true)}
									onClick={() => setDropdownOpen(true)}
									onBlur={handleScaleBlur}
									onKeyDown={handleScaleKeyDown}
									className="w-[70px] rounded-sm border border-white/30 bg-transparent px-1 py-0.5 text-center text-sm text-white outline-none focus:border-white/60"
								/>
								{dropdownOpen && (
									<div
										className={`absolute left-1/2 z-50 mt-1 w-[80px] -translate-x-1/2 rounded-md border border-white/20 py-1 shadow-lg ${localStorage.getItem("theme") === "dark" ? "bg-[#242424] text-white" : "bg-[#f8f8f8] text-black"}`}
									>
										{SCALE_OPTIONS.map((opt) => (
											<div
												key={opt.value}
												onMouseDown={(e) => e.preventDefault()}
												onClick={() => handleOptionClick(opt.value)}
												className="cursor-pointer px-2 py-1 text-center text-sm"
											>
												{opt.label}
											</div>
										))}
									</div>
								)}
							</div>
							<Button
								className="text-white!"
								type="text"
								icon={<ZoomInOutlined />}
								onClick={zoomIn}
								disabled={scale >= MAX_SCALE}
							/>
						</div>

						{/* Print */}
						<Popover placement="bottom" content="Print">
							<Button
								className="text-white!"
								type="text"
								icon={<PrinterOutlined />}
								onClick={handlePrint}
							/>
						</Popover>

						{/* Download */}
						{showDownload && (
							<Popover placement="bottom" content="Download">
								<Button
									className="text-white!"
									type="text"
									icon={<DownloadOutlined />}
									onClick={handleDownload}
								/>
							</Popover>
						)}
					</div>
				</div>

				{/* PDF Content */}
				<div
					ref={containerRef}
					className="flex min-h-0 flex-1 justify-center overflow-auto bg-[#525659]"
				>
					{fileUrl ? (
						<Document
							file={fileUrl}
							onLoadSuccess={onDocumentLoadSuccess}
							onLoadError={onDocumentLoadError}
							loading={
								<div className="flex justify-center p-10">
									<Spin size="large" />
								</div>
							}
						>
							{error ? (
								<Empty description={error} className="p-10" />
							) : (
								<div className="p-4">
									{Array.from(new Array(numPages), (_, index) => (
										<Page
											key={`page_${index + 1}`}
											pageNumber={index + 1}
											scale={scale}
											renderTextLayer={true}
											renderAnnotationLayer={true}
											onLoadSuccess={
												index === 0 ? onPageLoadSuccess : undefined
											}
										/>
									))}
								</div>
							)}
						</Document>
					) : (
						<Empty description="No URL provided" className="p-10" />
					)}
				</div>
			</div>
		</div>
	);
};

export default CustomPdfViewer;
